import React from "react";
import {
  Box,
  TextField,
  Chip,
  Autocomplete,
  Slider,
  Typography,
  Button,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { POSITION_COLORS, HEADER_SHADOW } from "../constants";
import { surfaceTokens } from "../config/theme";

export interface FilterBarProps {
  // Name search
  filterName: string;
  onFilterName: (v: string) => void;

  // Position chips (optional)
  positions?: string[];
  selectedPositions?: Set<string> | string[];
  onTogglePosition?: (pos: string) => void;

  // Rookie toggle (optional) — rendered next to the position chips
  rookieFilter?: boolean;
  onToggleRookie?: () => void;

  // Team autocomplete (optional)
  teamOptions?: string[];
  teamFilter?: string;
  onTeamFilter?: (v: string) => void;

  // Provider (platform) toggle (optional) — only meaningful with >1 platform.
  // Values: "all" | a provider label ("Underdog" | "DraftKings").
  providerOptions?: string[];
  providerFilter?: string;
  onProviderFilter?: (v: string) => void;

  // ADP range slider (optional)
  adpRangePending?: [number, number];
  onAdpRangeChange?: (_: unknown, v: number | number[]) => void;
  onAdpRangeCommit?: (_: unknown, v: number | number[]) => void;
  /** Upper bound of the ADP slider (UD=216, DK=240). Defaults to 216. */
  adpRangeMax?: number;
  /** Caption for the ADP slider (e.g. "UD ADP" | "DK ADP" | "Market ADP"). */
  adpRangeLabel?: string;

  // Active filter badge + reset
  activeFilterCount?: number;
  onReset?: () => void;

  // Right-side slot: result count text + action buttons (e.g. ColumnToggle)
  resultText?: string;
  actions?: React.ReactNode;
}

export function FilterBar({
  filterName,
  onFilterName,
  positions,
  selectedPositions,
  onTogglePosition,
  rookieFilter = false,
  onToggleRookie,
  teamOptions,
  teamFilter,
  onTeamFilter,
  providerOptions,
  providerFilter = "all",
  onProviderFilter,
  adpRangePending,
  onAdpRangeChange,
  onAdpRangeCommit,
  adpRangeMax = 216,
  adpRangeLabel = "UD ADP",
  activeFilterCount = 0,
  onReset,
  resultText,
  actions,
}: FilterBarProps) {
  const theme = useTheme();
  const tokens = surfaceTokens(theme.palette.mode === "dark");
  const warn = tokens.warn;
  const brandActive = tokens.brand;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const selectedSet: Set<string> =
    selectedPositions instanceof Set
      ? selectedPositions
      : new Set(selectedPositions ?? []);

  const content = (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        alignItems: { xs: "stretch", md: "flex-end" },
        py: 1.5,
        px: 2,
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        boxShadow: HEADER_SHADOW,
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      {/* Name search */}
      <TextField
        placeholder="Search players…"
        value={filterName}
        onChange={e => onFilterName(e.target.value)}
        size="small"
        sx={{ minWidth: { xs: 0, md: 160 }, width: { xs: "100%", sm: 220, md: 200 } }}
        inputProps={{ "aria-label": "Search players" }}
      />

      {/* Position chips + rookie toggle */}
      {((positions && onTogglePosition) || onToggleRookie) && (
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", width: { xs: "100%", md: "auto" } }}>
          {positions && onTogglePosition && positions.map(pos => {
            const posColor = POSITION_COLORS[pos] || "#888";
            const isSelected = selectedSet.has(pos);
            return (
              <Chip
                key={pos}
                label={pos}
                size="small"
                onClick={() => onTogglePosition(pos)}
                sx={{
                  height: 26,
                  fontWeight: 700,
                  fontSize: 11,
                  borderRadius: "6px",
                  border: `1.5px solid ${posColor}`,
                  bgcolor: isSelected ? posColor : "transparent",
                  color: isSelected ? "#fff" : posColor,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": { bgcolor: isSelected ? posColor : `${posColor}20` },
                }}
              />
            );
          })}
          {onToggleRookie && (
            <Chip
              label="Rookies"
              size="small"
              onClick={onToggleRookie}
              aria-pressed={rookieFilter}
              sx={{
                height: 26,
                fontWeight: 700,
                fontSize: 11,
                borderRadius: "6px",
                border: `1.5px solid ${warn}`,
                bgcolor: rookieFilter ? warn : "transparent",
                color: rookieFilter ? "#fff" : warn,
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": { bgcolor: rookieFilter ? warn : `${warn}20` },
              }}
            />
          )}
        </Box>
      )}

      {/* Provider (platform) toggle — only when more than one platform is loaded */}
      {providerOptions && providerOptions.length > 1 && onProviderFilter && (
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", width: { xs: "100%", md: "auto" } }}>
          {["all", ...providerOptions].map(opt => {
            const isSelected = providerFilter === opt;
            const label = opt === "all" ? "Both" : opt;
            return (
              <Chip
                key={opt}
                label={label}
                size="small"
                onClick={() => onProviderFilter(opt)}
                aria-pressed={isSelected}
                sx={{
                  height: 26,
                  fontWeight: 700,
                  fontSize: 11,
                  borderRadius: "6px",
                  border: `1.5px solid ${brandActive}`,
                  bgcolor: isSelected ? brandActive : "transparent",
                  color: isSelected ? "#fff" : brandActive,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": { bgcolor: isSelected ? brandActive : `${brandActive}20` },
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Team autocomplete — hidden when the import carries no team data
          (e.g. a DraftKings sync whose team fetch failed), so there's nothing
          empty to filter on. */}
      {teamOptions && teamOptions.length > 0 && onTeamFilter && (
        <Autocomplete
          options={["", ...teamOptions]}
          value={teamFilter ?? ""}
          onChange={(_, val) => onTeamFilter(val || "")}
          renderInput={params => (
            <TextField
              {...params}
              label="Team"
              size="small"
              inputProps={{ ...params.inputProps, placeholder: "" }}
            />
          )}
          sx={{ minWidth: { xs: 0, md: 120 }, width: { xs: "100%", sm: 180, md: 160 } }}
        />
      )}

      {/* ADP range slider */}
      {adpRangePending && onAdpRangeChange && onAdpRangeCommit && (
        <Box sx={{ minWidth: { xs: 0, sm: 240 }, width: { xs: "100%", sm: 280 }, maxWidth: { sm: 320 } }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            {adpRangeLabel} {adpRangePending[0]}–{adpRangePending[1] >= adpRangeMax ? "N/A" : adpRangePending[1]}
          </Typography>
          <Slider
            value={adpRangePending}
            min={0}
            max={adpRangeMax}
            step={1}
            onChange={onAdpRangeChange}
            onChangeCommitted={onAdpRangeCommit}
            valueLabelDisplay="auto"
            valueLabelFormat={(v, i) => (i === 1 && v >= adpRangeMax ? "N/A" : v)}
            marks={false}
            size="small"
          />
        </Box>
      )}

      {/* Reset button */}
      {activeFilterCount > 0 && onReset && (
        <Badge badgeContent={activeFilterCount} color="primary">
          <Button
            size="small"
            variant="outlined"
            onClick={onReset}
            startIcon={<RestartAltIcon />}
            sx={{ whiteSpace: "nowrap" }}
          >
            Reset
          </Button>
        </Badge>
      )}

      {/* Right-side: result text + action slot */}
      {(resultText || actions) && (
        <Box
          sx={{
            ml: { xs: 0, md: "auto" },
            width: { xs: "100%", md: "auto" },
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", md: "flex-start" },
            gap: 1,
          }}
        >
          {resultText && (
            <Typography
              variant="caption"
              color="text.secondary"
              aria-live="polite"
              aria-atomic="true"
            >
              {resultText}
            </Typography>
          )}
          {actions}
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Accordion
        defaultExpanded={false}
        sx={{ boxShadow: "none", border: "1px solid", borderColor: "divider" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={500}>
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>{content}</AccordionDetails>
      </Accordion>
    );
  }

  return content;
}
